/**
 * One undo service for the whole editor.
 *
 * Every editor that keeps history registers a `HistorySource` here. Ctrl+Z,
 * Ctrl+Shift+Z, the Edit menu and the Undo History panel all act on the
 * *active* source: the most recently used one that belongs to the focused
 * workspace view. So undo means the same thing in every editor, and the
 * History panel can show and jump through any of them.
 *
 * Editors that edit a document snapshot by snapshot use `DocHistory` (labelled
 * steps, coalescing of rapid edits such as slider drags, a size limit) through
 * `useDocHistory` in lib/use-doc-history.ts. The older document stores (scene,
 * model, pixel, sound, rig), which keep `past` / `future` arrays themselves,
 * join through `storeHistorySource`.
 *
 * Pure module (no DOM, no React) so it is testable in Node.
 */

export interface HistoryEntry {
  label: string;
  /** ms since epoch, when known. */
  time?: number;
}

export interface HistorySnapshot {
  /** Applied steps, oldest first. */
  past: readonly HistoryEntry[];
  /** Undone steps, next redo first. */
  future: readonly HistoryEntry[];
}

export interface HistorySource {
  id: string;
  label: string;
  /** Workspace view the source lives in (`scene`, `code`, ...). */
  view: string;
  snapshot(): HistorySnapshot;
  undo(): void;
  redo(): void;
  /** Change notification, so the History panel follows edits. */
  subscribe?(listener: () => void): () => void;
}

/* ------------------------------ doc history ------------------------------ */

interface Step<T> {
  state: T;
  label: string;
  time: number;
  key?: string;
}

/**
 * Snapshot undo stack for one document. `past` holds the state *before* each
 * applied step; `future` the state *after* each undone step.
 */
export class DocHistory<T> {
  private past: Step<T>[] = [];
  private future: Step<T>[] = [];
  private listeners = new Set<() => void>();

  constructor(
    readonly limit = 100,
    /** Steps with the same key closer together than this merge into one. */
    readonly coalesceMs = 700,
  ) {}

  /** Record a step: `before` is the document state the step started from. */
  record(before: T, label: string, key?: string, now = Date.now()): void {
    const last = this.past.at(-1);
    if (key !== undefined && last?.key === key && now - last.time < this.coalesceMs && this.future.length === 0) {
      last.time = now;
    } else {
      this.past.push({ state: before, label, time: now, ...(key !== undefined ? { key } : {}) });
      if (this.past.length > this.limit) this.past.splice(0, this.past.length - this.limit);
    }
    this.future = [];
    this.emit();
  }

  /** Step back from `current`; returns the state to show, or undefined at the start. */
  undo(current: T, now = Date.now()): T | undefined {
    const step = this.past.pop();
    if (!step) return undefined;
    this.future.unshift({ state: current, label: step.label, time: now });
    this.emit();
    return step.state;
  }

  redo(current: T, now = Date.now()): T | undefined {
    const step = this.future.shift();
    if (!step) return undefined;
    this.past.push({ state: current, label: step.label, time: now });
    this.emit();
    return step.state;
  }

  /** Stop the next edit from merging into the last step (e.g. after a drag ends). */
  seal(): void {
    const last = this.past.at(-1);
    if (last) delete last.key;
  }

  clear(): void {
    this.past = [];
    this.future = [];
    this.emit();
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  snapshot(): HistorySnapshot {
    return {
      past: this.past.map(({ label, time }) => ({ label, time })),
      future: this.future.map(({ label, time }) => ({ label, time })),
    };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of [...this.listeners]) listener();
  }
}

/** A HistorySource over a DocHistory whose document lives elsewhere. */
export function docHistorySource<T>(options: {
  id: string;
  label: string;
  view: string;
  history: DocHistory<T>;
  read: () => T;
  apply: (state: T) => void;
}): HistorySource {
  const { history, read, apply } = options;
  return {
    id: options.id,
    label: options.label,
    view: options.view,
    snapshot: () => history.snapshot(),
    undo: () => {
      const state = history.undo(read());
      if (state !== undefined) apply(state);
    },
    redo: () => {
      const state = history.redo(read());
      if (state !== undefined) apply(state);
    },
    subscribe: (listener) => history.subscribe(listener),
  };
}

/* ------------------------------ store adapter ----------------------------- */

interface PastFutureStore {
  getState(): { past: readonly unknown[]; future: readonly unknown[]; undo(): void; redo(): void };
  subscribe(listener: () => void): () => void;
}

/**
 * Adapt a document store that keeps `past` / `future` arrays and its own
 * undo/redo. Its steps carry no names, so they are numbered.
 */
export function storeHistorySource(options: {
  id: string;
  label: string;
  view: string;
  store: PastFutureStore;
  /** Name for step n (1-based); defaults to "Edit n". */
  stepLabel?: (n: number) => string;
}): HistorySource {
  const { store } = options;
  const name = options.stepLabel ?? ((n: number) => `Edit ${n}`);
  return {
    id: options.id,
    label: options.label,
    view: options.view,
    snapshot: () => {
      const { past, future } = store.getState();
      return {
        past: past.map((_, i) => ({ label: name(i + 1) })),
        future: future.map((_, i) => ({ label: name(past.length + i + 1) })),
      };
    },
    undo: () => store.getState().undo(),
    redo: () => store.getState().redo(),
    subscribe: (listener) => {
      // Only history changes matter; document stores update on every keystroke.
      let { past, future } = store.getState();
      return store.subscribe(() => {
        const next = store.getState();
        if (next.past === past && next.future === future) return;
        past = next.past;
        future = next.future;
        listener();
      });
    },
  };
}

/* -------------------------------- service -------------------------------- */

interface Registered {
  source: HistorySource;
  touched: number;
  unsubscribe?: () => void;
}

export class UndoService {
  private sources = new Map<string, Registered>();
  private listeners = new Set<() => void>();
  private clock = 0;
  /** Bumped on every change; the History panel reads it. */
  version = 0;

  /** Add a source (it becomes the most recent for its view). Returns the disposer. */
  register(source: HistorySource): () => void {
    this.sources.get(source.id)?.unsubscribe?.();
    const entry: Registered = { source, touched: ++this.clock };
    entry.unsubscribe = source.subscribe?.(() => {
      entry.touched = ++this.clock;
      this.emit();
    });
    this.sources.set(source.id, entry);
    this.emit();
    return () => {
      if (this.sources.get(source.id) !== entry) return;
      entry.unsubscribe?.();
      this.sources.delete(source.id);
      this.emit();
    };
  }

  /** Mark a source as the one the user is working in (focus, click). */
  touch(id: string): void {
    const entry = this.sources.get(id);
    if (!entry) return;
    entry.touched = ++this.clock;
    this.emit();
  }

  get(id: string): HistorySource | undefined {
    return this.sources.get(id)?.source;
  }

  /** All sources, most recently used first. */
  list(): HistorySource[] {
    return [...this.sources.values()].sort((a, b) => b.touched - a.touched).map((e) => e.source);
  }

  /** The source Undo applies to in `view` (the most recent one there). */
  active(view: string | null): HistorySource | null {
    let best: Registered | null = null;
    for (const entry of this.sources.values()) {
      if (view !== null && entry.source.view !== view) continue;
      if (!best || entry.touched > best.touched) best = entry;
    }
    return best?.source ?? null;
  }

  undo(view: string | null): boolean {
    const source = this.active(view);
    if (!source || source.snapshot().past.length === 0) return false;
    source.undo();
    this.touch(source.id);
    return true;
  }

  redo(view: string | null): boolean {
    const source = this.active(view);
    if (!source || source.snapshot().future.length === 0) return false;
    source.redo();
    this.touch(source.id);
    return true;
  }

  /**
   * Move a source to the state after `applied` steps (0 = original document),
   * undoing or redoing as many times as needed.
   */
  jump(id: string, applied: number): void {
    const source = this.get(id);
    if (!source) return;
    const { past, future } = source.snapshot();
    const target = Math.max(0, Math.min(applied, past.length + future.length));
    let delta = target - past.length;
    while (delta < 0) {
      source.undo();
      delta++;
    }
    while (delta > 0) {
      source.redo();
      delta--;
    }
    this.touch(id);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    this.version++;
    for (const listener of [...this.listeners]) listener();
  }
}

/** The editor-wide service. */
export const undoService = new UndoService();
